import { Box, Html } from '@react-three/drei'
import type { ThreeElements } from '@react-three/fiber'
import { useFrame } from '@react-three/fiber'
import { useMemo, useState } from 'react'
import { MathUtils, QuadraticBezierCurve3, Vector3 } from 'three'

import { BufferStopModel, RailroadCrossingModel } from '@components/models'
import { Goal } from '@components/ui'
import type { Direction } from '@config'
import { parsePort, type CellNode } from '@engine'
import { GamePhase, useGameStore } from '@stores'
import { sampleSplines, SPLINE_HEIGHT, SPLINES_COUNT, useHelpersControls } from '@utils'

import { AnimatedSwitchTrack } from './AnimatedSwitchTrack'

const CROSSING_OFFSETS: Record<Direction, [number, number, number]> = {
  N: [0.45, 0, -1],
  S: [-0.45, 0, 1],
  E: [1, 0, 0.45],
  W: [-1, 0, -0.45],
}

const CROSSING_ROTATIONS: Record<Direction, number> = {
  N: MathUtils.degToRad(180),
  S: MathUtils.degToRad(0),
  E: MathUtils.degToRad(90),
  W: MathUtils.degToRad(-90),
}

const GOAL_OFFSETS: Record<Direction, [number, number, number]> = {
  N: [2, 0, 0],
  S: [1.5, 0, 1.5],
  E: [-1.5, 0, -1.5],
  W: [1.5, 0, -1.5],
}

interface CrossingControllerProps {
  x: number
  y: number
  exit: Direction
}

function CrossingController({ x, y, exit }: CrossingControllerProps) {
  const phase = useGameStore(state => state.phase)
  const goal = useGameStore(state => state.level.goal)

  const goalIsEmpty = !goal.sequence || goal.sequence.length === 0
  const goalOffset = goal.offset || new Vector3(0, 0, 0)

  // const grid = useGameStore(state => state.network?.grid)
  const [crossingOpen, setCrossingOpen] = useState(false)

  // const extraZ = useMemo(() => {
  //   if (!grid || y === 0) return -y

  //   const allClear = Array.from({ length: y }, (_, i) => i).every(rowIdx => {
  //     const row = grid[rowIdx]
  //     if (!row) return true
  //     return exit === 'W'
  //       ? !row[x] && !row[x + 1] && !row[x + 2]
  //       : exit === 'E'
  //         ? !row[x] && !row[x - 1] && !row[x - 2]
  //         : !row[x]
  //   })

  //   return allClear ? 0 : -y * 0.7
  // }, [grid, x, y, exit])

  useFrame(() => {
    const { network, levelComplete } = useGameStore.getState()

    if (!goalIsEmpty && !levelComplete) {
      if (crossingOpen) setCrossingOpen(false)
      return
    }

    const loco = network?.locomotive
    if (!loco) {
      if (!crossingOpen) setCrossingOpen(true)
      return
    }

    if (loco.edgeId.startsWith('ESCAPE:')) {
      if (!crossingOpen) setCrossingOpen(true)
      return
    }

    const edge = network?.edges.get(loco.edgeId)
    if (!edge) return

    const portA = parsePort(edge.a)
    const portB = parsePort(edge.b)
    const next = (portA.x === x && portA.y === y) || (portB.x === x && portB.y === y)
    if (next !== crossingOpen) setCrossingOpen(next)
  })

  return (
    <>
      <RailroadCrossingModel
        open={crossingOpen}
        position={CROSSING_OFFSETS[exit]}
        rotation-y={CROSSING_ROTATIONS[exit]}
      />
      {phase >= GamePhase.PLAYING && (
        <Goal
          position-x={GOAL_OFFSETS[exit][0] + goalOffset.x}
          position-z={
            GOAL_OFFSETS[exit][2] + goalOffset.z
            // + extraZ
          }
          rotation-y={MathUtils.degToRad(-35)}
        />
      )}
    </>
  )
}

export type TrackCellProps = CellNode &
  Pick<ThreeElements['group'], 'position'> & {
    curves: QuadraticBezierCurve3[]
  }

export function TrackCell({ position, curves, ...cell }: TrackCellProps) {
  const { grid } = useHelpersControls()

  const isSwitch = cell.type === 'switch'
  const isBumper = cell.type === 'bumper'
  const activeIndex = cell.active ?? 0

  const bumperStopPose = useMemo(() => {
    if (!isBumper) return null
    const count = SPLINES_COUNT + 4
    const poses = sampleSplines(curves[0], count)
    return poses[poses.length - 1]
  }, [isBumper, curves])

  return (
    <group position={position}>
      {grid && (
        <Box scale={0.5} material-color={cell.decoration ? 'green' : 'red'}>
          <Html center className="w-10 bg-white text-center font-mono">
            {cell.x}, {cell.y}
          </Html>
        </Box>
      )}

      {bumperStopPose && (
        <group position-y={SPLINE_HEIGHT * 0.5}>
          <BufferStopModel
            position={[bumperStopPose.position.x, 0, bumperStopPose.position.z]}
            rotation-y={bumperStopPose.rotationY}
          />
        </group>
      )}

      {isSwitch && (
        <AnimatedSwitchTrack x={cell.x} y={cell.y} shapes={cell.shapes} activeIndex={activeIndex} />
      )}

      {cell.exit && <CrossingController x={cell.x} y={cell.y} exit={cell.exit} />}
    </group>
  )
}
